import { TooltipProvider } from '@newfront-insurance/core-ui/v2';
import type { TRPCClientHooks } from '@newfront-insurance/data-layer-client';
import { HTTPError, NextApiErrorBoundary, isTRPCClientError } from '@newfront-insurance/data-layer-client';
import { NewfrontApolloProvider } from '@newfront-insurance/graphql-frontend';
import {
  AuthProviderType,
  SSRBoundary,
  type AuthProviderContext,
  type AuthProviderProps,
  LoginBoundary,
} from '@newfront-insurance/next-auth';
import type { LoginBoundaryProps } from '@newfront-insurance/next-auth';
import { RouterProvider } from '@newfront-insurance/next-router-provider';
import type { Provider } from '@newfront-insurance/react-provision';
import type { ConfigType } from '@newfront-insurance/shared-public-config';
import type { QueryObserverOptions } from '@tanstack/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type AnyRouter } from '@trpc/server';
import dynamic from 'next/dynamic';
import type { JSX } from 'react';
import { type ReactNode } from 'react';
import { Suspense } from 'react';
import * as React from 'react';
import { preconnect } from 'react-dom';

import { AnalyticsProvider } from './providers/analytics';
import { AuthNextSwrProvider } from './providers/auth-swr';
import { ConditionalAuthProvider } from './providers/conditional-auth-provider';
import { DatadogWrapper } from './providers/datadog-wrapper';
import { FeatureFlagProvider } from './providers/feature-flags';

export interface DdRumConfig {
  applicationId: string;
  clientToken: string;
  appName: string;
}

interface AppProvidersProps {
  children: ReactNode;
  config: ConfigType;
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
  authProviderType?: AuthProviderType;
  withLoginBoundary?: boolean;
  customLoginBoundary?: React.ComponentType<LoginBoundaryProps>;
  trpcProvider: Provider<TRPCClientHooks<AnyRouter>>;
  useConditionalAuthProvider: boolean;
  analyticsAppName: string;
}

const NotificationProvider = dynamic(
  async () => {
    const module = await import('@newfront-insurance/next-notifications');
    return module.NotificationProvider;
  },
  {
    ssr: false,
  },
);

function ReactNotificationProvider({ children }: { children: ReactNode }): JSX.Element {
  return <NotificationProvider>{children}</NotificationProvider>;
}

const RETRYABLE_STATUS_CODES = [418, 502, 503, 504];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      throwOnError: (_error, query) => {
        return !query.state.data && (query.options as QueryObserverOptions).suspense === true;
      },
      retry(failureCount, error) {
        if (failureCount >= 3) {
          return false;
        }

        if (isTRPCClientError(error) && error.cause instanceof HTTPError) {
          const statusCode = error.cause.response.status;

          return RETRYABLE_STATUS_CODES.includes(statusCode);
        }

        return true;
      },
    },
  },
});

/**
 * Used to apply a custom name and service to a view
 */
export function GeneralAppProviders({
  children,
  config,
  authProvider: AuthProvider,
  trpcProvider: TRPCProvider,
  authProviderType = AuthProviderType.KEYCLOAK_JS,
  withLoginBoundary = true,
  customLoginBoundary = undefined,
  useConditionalAuthProvider,
  analyticsAppName,
}: AppProvidersProps): JSX.Element {
  preconnect(config.API.AUTH);

  return (
    <NextApiErrorBoundary>
      <SSRBoundary>
        <RouterProvider>
          <ConditionalAuthProvider
            useConditionalAuthProvider={useConditionalAuthProvider}
            authProvider={AuthProvider}
            authProviderType={authProviderType}
          >
            <LoginBoundary
              authProvider={AuthProvider}
              isEnabled={withLoginBoundary}
              CustomLoginBoundary={customLoginBoundary}
            >
              <ReactNotificationProvider>
                <NewfrontApolloProvider authProvider={AuthProvider} config={config}>
                  <Suspense fallback={null}>
                    <QueryClientProvider client={queryClient}>
                      <FeatureFlagProvider config={config} authProvider={AuthProvider}>
                        <DatadogWrapper authProvider={AuthProvider}>
                          <AuthNextSwrProvider authProvider={AuthProvider}>
                            <AnalyticsProvider
                              authProvider={AuthProvider}
                              config={config}
                              analyticsAppName={analyticsAppName}
                            >
                              <TRPCProvider>
                                <TooltipProvider>{children}</TooltipProvider>
                              </TRPCProvider>
                            </AnalyticsProvider>
                          </AuthNextSwrProvider>
                        </DatadogWrapper>
                      </FeatureFlagProvider>
                    </QueryClientProvider>
                  </Suspense>
                </NewfrontApolloProvider>
              </ReactNotificationProvider>
            </LoginBoundary>
          </ConditionalAuthProvider>
        </RouterProvider>
      </SSRBoundary>
    </NextApiErrorBoundary>
  );
}
